import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Check,
  Clock3,
  Image as ImageIcon,
  Ruler,
} from "lucide-react";

import Navbar from "../../components/site/Navbar";
import Footer from "../../components/site/Footer";
import { useAuth } from "../../components/auth/AuthGate";
import { useGarment } from "../../hooks/useGarment";
import { submitTailorQuoteRequest } from "../../services/tailorQuoteService";
import TailorQuoteContactFields, { emptyQuoteContact, isQuoteContactValid } from "../../components/quotes/TailorQuoteContactFields";

function readDesignDraft() {
  try {
    const stored = sessionStorage.getItem("stichb_design_draft");

    if (!stored) {
      return null;
    }

    return JSON.parse(stored);
  } catch (error) {
    console.error("Unable to read design draft:", error);
    return null;
  }
}

function sourceLabel(source) {
  switch (source) {
    case "catalog_design":
      return "StichB Design";
    case "uploaded_design":
      return "Uploaded Reference";
    case "ai_design":
      return "AI Design Brief";
    default:
      return "Selected Design";
  }
}

export default function DesignConfirmationPage() {
  const {
    categoryId,
    sectionId,
    garmentId,
  } = useParams();

  const navigate = useNavigate();
  const { user, openAuth } = useAuth();

  const {
    garment,
    garmentLoading,
  } = useGarment(garmentId);

  const [draft, setDraft] = useState(() => readDesignDraft());
  const [requestingQuote, setRequestingQuote] = useState(false);
  const [quoteRequestError, setQuoteRequestError] = useState("");
  const [quoteRequestId, setQuoteRequestId] = useState(
    () => readDesignDraft()?.quoteRequestId || ""
  );
  const [quoteContact, setQuoteContact] = useState(() => emptyQuoteContact());

  useEffect(() => {
    setDraft(readDesignDraft());
  }, []);

  useEffect(() => {
    setQuoteContact((current) => ({
      ...current,
      phone: current.phone || String(user?.phoneNumber || "").replace(/\D/g, "").slice(-10),
    }));
  }, [user]);

  const title = useMemo(() => {
    return (
      draft?.designName ||
      draft?.fileName ||
      draft?.style ||
      "Your selected design"
    );
  }, [draft]);

  const description = useMemo(() => {
    return (
      draft?.description ||
      draft?.notes ||
      draft?.prompt ||
      "Your design selection is ready for confirmation."
    );
  }, [draft]);

  const imageUrl =
    draft?.imageUrl ||
    draft?.previewUrl ||
    "";

  const continueToMeasurements = () => {
    const existingBooking = (() => {
      try {
        return JSON.parse(
          sessionStorage.getItem("stichb_booking_draft") || "{}"
        );
      } catch {
        return {};
      }
    })();

    const bookingDraft = {
      ...existingBooking,
      categoryId,
      sectionId,
      garmentId,
      garmentName: garment?.name || draft?.garmentName || "",
      design: draft,
      updatedAt: new Date().toISOString(),
    };

    sessionStorage.setItem(
      "stichb_booking_draft",
      JSON.stringify(bookingDraft)
    );

    navigate(
      `/category/${categoryId}/section/${sectionId}/item/${garmentId}/measurements`
    );
  };

  const requestTailorQuotation = async () => {
    if (!user) {
      openAuth();
      return;
    }

    if (quoteRequestId) return;

    if (!draft?.imageDataUrl && !draft?.previewUrl) {
      setQuoteRequestError("The uploaded design image is missing. Please upload again.");
      return;
    }

    setRequestingQuote(true);
    setQuoteRequestError("");

    try {
      const result = await submitTailorQuoteRequest({
        customer: {
          name: user.displayName || "",
          email: user.email || "",
          phone: quoteContact.phone,
          uid: user.uid,
        },
        garmentName: garment?.name || draft?.garmentName || "Your garment",
        designImageDataUrl: draft?.imageDataUrl || draft?.previewUrl || "",
        designFileName: draft?.fileName || "uploaded-design",
        notes: draft?.notes || "",
        serviceType: "my_design",
        ...quoteContact,
        garmentId: draft?.garmentId || garmentId || "",
        startingPrice: Number(
          draft?.basePrice ||
          garment?.startingPrice ||
          garment?.basePrice ||
          0
        ),
        requestDetails: {
          design: draft,
          startingPrice: Number(
            draft?.basePrice ||
            garment?.startingPrice ||
            garment?.basePrice ||
            0
          ),
        },
      });

      setQuoteRequestId(result.id);
      sessionStorage.setItem(
        "stichb_tailor_quote_request",
        JSON.stringify({
          requestId: result.id,
          garmentName: garment?.name || draft?.garmentName || "Your garment",
          submittedAt: new Date().toISOString(),
        })
      );
    } catch (error) {
      setQuoteRequestError(
        error?.message || "Unable to request a tailor quotation right now."
      );
    } finally {
      setRequestingQuote(false);
    }
  };

  if (!draft) {
    return (
      <div className="min-h-screen bg-[#FFF8F2]">
        <Navbar onBook={() => navigate("/#services")} />

        <main className="pt-32 pb-24">
          <section className="max-w-[800px] mx-auto px-6 md:px-12">
            <div className="card-soft p-10 text-center">
              <ImageIcon
                size={38}
                className="mx-auto text-[#F46524]"
              />

              <h1 className="font-display text-3xl mt-5">
                No design selected
              </h1>

              <p className="text-[#5b5a56] mt-3">
                Choose a StichB design, upload a reference, or create an AI
                design brief first.
              </p>

              <button
                type="button"
                onClick={() =>
                  navigate(
                    `/category/${categoryId}/section/${sectionId}/item/${garmentId}/designs`
                  )
                }
                className="btn-primary mt-7"
              >
                Choose a design
              </button>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFF8F2]">
      <Navbar onBook={() => navigate("/#services")} />

      <main className="pt-28 pb-24">
        <section className="max-w-[1100px] mx-auto px-6 md:px-12">
          <button
            type="button"
            onClick={() =>
              navigate(
                `/category/${categoryId}/section/${sectionId}/item/${garmentId}/designs`
              )
            }
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#F46524]"
          >
            <ArrowLeft size={16} />
            Change design
          </button>

          <div className="mt-8">
            <div className="font-label text-[#F46524] mb-4">
              Confirm Your Design
            </div>

            <h1 className="font-display text-4xl md:text-6xl tracking-tight">
              Review before continuing
            </h1>

            <p className="text-[#5b5a56] mt-5 text-lg">
              Confirm your design choice before adding measurements and fitting
              preferences.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="card-soft overflow-hidden">
              <div className="aspect-[4/5] bg-[#FFF3E8] flex items-center justify-center">
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt={title}
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <div className="text-center px-8">
                    <ImageIcon
                      size={44}
                      className="mx-auto text-[#F46524]"
                    />

                    <div className="font-display text-2xl mt-5">
                      {sourceLabel(draft.source)}
                    </div>

                    <p className="text-sm text-[#5b5a56] mt-2">
                      The final visual will be reviewed during order
                      confirmation.
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="card-soft p-7">
              <div className="inline-flex items-center gap-2 rounded-full bg-[#FFEDDD] px-4 py-2 text-sm font-semibold text-[#F46524]">
                <Check size={15} />
                {sourceLabel(draft.source)}
              </div>

              <h2 className="font-display text-3xl mt-6">
                {title}
              </h2>

              <div className="text-sm text-[#8a8781] mt-2">
                Garment:{" "}
                <strong className="text-[#2B2B2B]">
                  {garmentLoading
                    ? "Loading..."
                    : garment?.name || draft?.garmentName || "Garment"}
                </strong>
              </div>

              <p className="text-[#5b5a56] leading-relaxed mt-6 whitespace-pre-line">
                {description}
              </p>

              {draft.colour ? (
                <div className="mt-5 text-sm">
                  <span className="font-semibold">Colour:</span>{" "}
                  {draft.colour}
                </div>
              ) : null}

              {draft.fabric ? (
                <div className="mt-2 text-sm">
                  <span className="font-semibold">Fabric:</span>{" "}
                  {draft.fabric}
                </div>
              ) : null}

              <div className="mt-8 rounded-2xl bg-[#FFF3E8] p-5">
                <div className="font-semibold text-[#2B2B2B]">
                  Next: Measurements
                </div>

                <p className="text-sm text-[#5b5a56] mt-2">
                  Add new measurements, select saved measurements, or request
                  home measurement assistance.
                </p>
              </div>

              {draft.source === "uploaded_design" ? (
                <>
                  <div className="mt-6 rounded-2xl bg-[#FFF3E8] p-5 text-sm text-[#5b5a56]">
                    <div className="font-semibold text-[#2B2B2B] mb-2">
                      Quote-first custom design flow
                    </div>
                    <p>
                      Your uploaded reference will be reviewed by the tailor team, who will call at your selected time and share the final quote for approval.
                    </p>
                  </div>

                  {quoteRequestError ? (
                    <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                      {quoteRequestError}
                    </div>
                  ) : null}

                  {!quoteRequestId ? (
                    <div className="mt-5">
                      <TailorQuoteContactFields value={quoteContact} onChange={setQuoteContact} />
                    </div>
                  ) : null}

                  {quoteRequestId ? (
                    <div className="mt-4 rounded-2xl border border-[#CFEAD8] bg-[#ECF9F0] px-4 py-4 text-sm text-[#1F5E39]">
                      <div className="flex items-center gap-2 font-semibold">
                        <Clock3 size={16} />
                        Tailor quotation requested
                      </div>
                      <p className="mt-2">
                        Your request has been sent to the tailor team. They will review the design and call you at the selected time.
                      </p>
                      <p className="mt-2">
                        You can safely leave this page. Find this request under Orders → Quotations and continue after the tailor calls you.
                      </p>
                      <button
                        type="button"
                        onClick={() => navigate(`/account/quotes/${quoteRequestId}`)}
                        className="mt-3 font-semibold text-[#F46524]"
                      >
                        View saved quotation
                      </button>
                    </div>
                  ) : null}

                  <button
                    type="button"
                    onClick={requestTailorQuotation}
                    disabled={requestingQuote || Boolean(quoteRequestId) || !isQuoteContactValid(quoteContact)}
                    className="btn-primary w-full justify-center mt-7 disabled:opacity-60"
                  >
                    <Clock3 size={18} />
                    {requestingQuote ? "Sending request..." : quoteRequestId ? "Quotation requested" : "Get tailor quotation"}
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={continueToMeasurements}
                  className="btn-primary w-full justify-center mt-7"
                >
                  <Ruler size={18} />
                  Continue to measurements
                </button>
              )}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
